import {
  Box,
  Container,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  CheckboxGroup,
  Button,
  useToast,
  Text,
  SimpleGrid,
  Link,
  FormErrorMessage,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "phosphor-react";
import { CheckCircle } from "phosphor-react";

interface FormData {
  firstName: string;
  lastName: string;
  nickname: string;
  email: string;
  userTypes: string[];
  socialLinks: {
    x: string;
    facebook: string;
    instagram: string;
    youtube: string;
    twitch: string;
    tiktok: string;
  };
  acceptTerms: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  userTypes?: string;
  acceptTerms?: string;
}

const EarlyAccess = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    nickname: "",
    email: "",
    userTypes: [],
    socialLinks: {
      x: "",
      facebook: "",
      instagram: "",
      youtube: "",
      twitch: "",
      tiktok: "",
    },
    acceptTerms: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = "You must accept the terms and conditions";
    }
    if (formData.userTypes.length === 0) {
      newErrors.userTypes = "Please select at least one option";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.firstName !== "" &&
      formData.lastName !== "" &&
      formData.email !== "" &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.userTypes.length > 0 &&
      formData.acceptTerms
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbz7mPgTlqm5bteWLWOkshPlyKTM7l3fK6LLfDLoBjPCgHreKLQynFVvdLjDppjt8I4XIg/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "text/plain",
          },
          body: JSON.stringify(formData),
        }
      );

      // With no-cors mode, we won't get a readable status
      // If we get here without throwing, consider it a success
      setIsSubmitted(true);
      setFormData({
        firstName: "",
        lastName: "",
        nickname: "",
        email: "",
        userTypes: [],
        socialLinks: {
          x: "",
          facebook: "",
          instagram: "",
          youtube: "",
          twitch: "",
          tiktok: "",
        },
        acceptTerms: false,
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description:
          "There was an error submitting your registration. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.900" py={20}>
      <Container maxW="container.md">
        <VStack spacing={8} align="stretch">
          <HStack spacing={4}>
            <Button
              leftIcon={<Icon as={ArrowLeft} weight="bold" />}
              variant="ghost"
              color="white"
              onClick={() => navigate("/")}
              _hover={{ bg: "whiteAlpha.200" }}
            >
              Back to home
            </Button>
          </HStack>

          {isSubmitted ? (
            <VStack spacing={8} align="center" py={20}>
              <Icon
                as={CheckCircle}
                weight="fill"
                color="green.400"
                boxSize={20}
              />
              <Heading color="white" textAlign="center" size="2xl">
                Thank you for registering!
              </Heading>
              <Text
                color="gray.300"
                fontSize="xl"
                textAlign="center"
                maxW="600px"
              >
                We're excited to have you join Virtual Golf Society. We'll be in
                touch soon with more information.
              </Text>
              <Button
                size="lg"
                onClick={() => navigate("/")}
                variant="ghost"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Return to home
              </Button>
            </VStack>
          ) : (
            <>
              <Heading
                color="oxygon.primary"
                textAlign="center"
                bgGradient="linear(to-r, oxygon.primary, oxygon.secondary)"
                bgClip="text"
              >
                Create Free Account
              </Heading>

              <Box
                as="form"
                onSubmit={handleSubmit}
                bg="gray.800"
                p={8}
                borderRadius="xl"
              >
                <VStack spacing={6}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                    <FormControl isRequired isInvalid={!!errors.firstName}>
                      <FormLabel color="white">First name</FormLabel>
                      <Input
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.lastName}>
                      <FormLabel color="white">Last name</FormLabel>
                      <Input
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel color="white">
                      Nickname/team/organization name
                    </FormLabel>
                    <Input
                      value={formData.nickname}
                      onChange={(e) =>
                        setFormData({ ...formData, nickname: e.target.value })
                      }
                      bg="gray.700"
                      color="white"
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel color="white">Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      bg="gray.700"
                      color="white"
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.userTypes}>
                    <FormLabel color="white">I am a: *</FormLabel>
                    <CheckboxGroup
                      value={formData.userTypes}
                      onChange={(values) => {
                        setFormData({
                          ...formData,
                          userTypes: values as string[],
                        });
                      }}
                    >
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Checkbox
                          value="home_sim_owner"
                          isChecked={formData.userTypes.includes(
                            "home_sim_owner"
                          )}
                          onChange={(e) => {
                            const newTypes = e.target.checked
                              ? [...formData.userTypes, "home_sim_owner"]
                              : formData.userTypes.filter(
                                  (type) => type !== "home_sim_owner"
                                );
                            setFormData({ ...formData, userTypes: newTypes });
                          }}
                          sx={{
                            ".chakra-checkbox__control": {
                              borderColor: "whiteAlpha.400",
                              _checked: {
                                bg: "white",
                                borderColor: "white",
                                color: "gray.900",
                              },
                            },
                          }}
                        >
                          <Text color="white">Home simulator owner</Text>
                        </Checkbox>
                        <Checkbox
                          value="venue_or_studio"
                          isChecked={formData.userTypes.includes(
                            "venue_or_studio"
                          )}
                          onChange={(e) => {
                            const newTypes = e.target.checked
                              ? [...formData.userTypes, "venue_or_studio"]
                              : formData.userTypes.filter(
                                  (type) => type !== "venue_or_studio"
                                );
                            setFormData({ ...formData, userTypes: newTypes });
                          }}
                          sx={{
                            ".chakra-checkbox__control": {
                              borderColor: "whiteAlpha.400",
                              _checked: {
                                bg: "white",
                                borderColor: "white",
                                color: "gray.900",
                              },
                            },
                          }}
                        >
                          <Text color="white">Indoor venue / school</Text>
                        </Checkbox>
                        <Checkbox
                          value="creator_or_community"
                          isChecked={formData.userTypes.includes(
                            "creator_or_community"
                          )}
                          onChange={(e) => {
                            const newTypes = e.target.checked
                              ? [...formData.userTypes, "creator_or_community"]
                              : formData.userTypes.filter(
                                  (type) => type !== "creator_or_community"
                                );
                            setFormData({ ...formData, userTypes: newTypes });
                          }}
                          sx={{
                            ".chakra-checkbox__control": {
                              borderColor: "whiteAlpha.400",
                              _checked: {
                                bg: "white",
                                borderColor: "white",
                                color: "gray.900",
                              },
                            },
                          }}
                        >
                          <Text color="white">Creator / community</Text>
                        </Checkbox>
                        <Checkbox
                          value="friend_group"
                          isChecked={formData.userTypes.includes(
                            "friend_group"
                          )}
                          onChange={(e) => {
                            const newTypes = e.target.checked
                              ? [...formData.userTypes, "friend_group"]
                              : formData.userTypes.filter(
                                  (type) => type !== "friend_group"
                                );
                            setFormData({ ...formData, userTypes: newTypes });
                          }}
                          sx={{
                            ".chakra-checkbox__control": {
                              borderColor: "whiteAlpha.400",
                              _checked: {
                                bg: "white",
                                borderColor: "white",
                                color: "gray.900",
                              },
                            },
                          }}
                        >
                          <Text color="white">Competitive friend group</Text>
                        </Checkbox>
                      </SimpleGrid>
                    </CheckboxGroup>
                    <FormErrorMessage>{errors.userTypes}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel color="white">Social media profiles</FormLabel>
                    <VStack spacing={4}>
                      <Input
                        placeholder="X URL"
                        value={formData.socialLinks.x}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialLinks: {
                              ...formData.socialLinks,
                              x: e.target.value,
                            },
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <Input
                        placeholder="Facebook URL"
                        value={formData.socialLinks.facebook}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialLinks: {
                              ...formData.socialLinks,
                              facebook: e.target.value,
                            },
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <Input
                        placeholder="Instagram URL"
                        value={formData.socialLinks.instagram}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialLinks: {
                              ...formData.socialLinks,
                              instagram: e.target.value,
                            },
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <Input
                        placeholder="TikTok URL"
                        value={formData.socialLinks.tiktok}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialLinks: {
                              ...formData.socialLinks,
                              tiktok: e.target.value,
                            },
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <Input
                        placeholder="YouTube URL"
                        value={formData.socialLinks.youtube}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialLinks: {
                              ...formData.socialLinks,
                              youtube: e.target.value,
                            },
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                      <Input
                        placeholder="Twitch URL"
                        value={formData.socialLinks.twitch}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            socialLinks: {
                              ...formData.socialLinks,
                              twitch: e.target.value,
                            },
                          })
                        }
                        bg="gray.700"
                        color="white"
                      />
                    </VStack>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.acceptTerms}>
                    <Checkbox
                      isChecked={formData.acceptTerms}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          acceptTerms: e.target.checked,
                        })
                      }
                      sx={{
                        ".chakra-checkbox__control": {
                          borderColor: "whiteAlpha.400",
                          _checked: {
                            bg: "white",
                            borderColor: "white",
                            color: "gray.900",
                          },
                        },
                      }}
                    >
                      <Text color="white">
                        I accept the{" "}
                        <Link
                          color="oxygon.primary"
                          href="/terms"
                          target="_blank"
                        >
                          terms and conditions
                        </Link>
                      </Text>
                    </Checkbox>
                    <FormErrorMessage>{errors.acceptTerms}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    bg="white"
                    color="gray.900"
                    size="lg"
                    width="full"
                    isLoading={isSubmitting}
                    isDisabled={!isFormValid()}
                    _hover={{
                      bg: "gray.100",
                    }}
                    _disabled={{
                      bg: "whiteAlpha.300",
                      color: "whiteAlpha.700",
                      cursor: "not-allowed",
                    }}
                  >
                    Submit
                  </Button>
                </VStack>
              </Box>
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default EarlyAccess;
